import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { isSuperAdmin } from "../../utils/roles";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

function MetricCard({ icon, title, value, helper }) {
  return (
    <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)", height: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ width: 48, height: 48, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
            {icon}
          </Box>
          <Box>
            <Typography sx={{ color: "rgba(25,36,47,0.62)", fontWeight: 800 }}>{title}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>{value}</Typography>
          </Box>
        </Stack>
        {helper && <Typography sx={{ mt: 2, color: "rgba(25,36,47,0.6)" }}>{helper}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function DashboardHomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { projects, activeProject, loadProjects, loading, error } = useProjects();
  const superAdmin = isSuperAdmin(user);

  React.useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
              Dashboard
            </Typography>
            <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
              Hola {user?.name}. {superAdmin ? "Esta es tu entrada al superpanel de WebProfit." : "Este es el panel de tu proyecto dentro de WebProfit."}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {superAdmin && (
              <Button variant="outlined" onClick={() => navigate("/dashboard/superpanel")} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, px: 3 }}>
                Superpanel
              </Button>
            )}
            <Button variant="contained" onClick={() => navigate("/dashboard/proyectos")} sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, px: 3 }}>
              {superAdmin ? "Crear / ver proyectos" : "Ver mis proyectos"}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2.5}>
        {superAdmin && (
          <Grid item xs={12} md={4}>
            <MetricCard icon={<AdminPanelSettingsRoundedIcon />} title="Modo" value="Superadmin" helper="Podés aprobar usuarios, crear clientes y configurar planes mensuales." />
          </Grid>
        )}
        <Grid item xs={12} md={superAdmin ? 4 : 4}>
          <MetricCard icon={<StorefrontRoundedIcon />} title={superAdmin ? "Proyectos totales" : "Mis proyectos"} value={loading ? "..." : projects.length} helper="Cada proyecto puede tener su propia DB y configuración." />
        </Grid>
        <Grid item xs={12} md={superAdmin ? 4 : 4}>
          <MetricCard icon={<Inventory2RoundedIcon />} title="Proyecto activo" value={activeProject?.name || "Ninguno"} helper="El panel opera sobre el proyecto seleccionado." />
        </Grid>
        {!superAdmin && (
          <Grid item xs={12} md={4}>
            <MetricCard icon={<SettingsRoundedIcon />} title="Acceso" value="Cliente" helper="Ves solo las secciones habilitadas para tu proyecto." />
          </Grid>
        )}
      </Grid>

      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ xs: "flex-start", md: "center" }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                {superAdmin ? "Orden de trabajo del superpanel" : "Tu operación dentro de WebProfit"}
              </Typography>
              <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 1 }}>
                {superAdmin
                  ? "Primero aprobás usuarios, después creás o asignás proyectos, y finalmente definís el plan mensual de cada cliente."
                  : "Desde acá vas a poder administrar productos, ver estadísticas y configurar lo que WebProfit tenga habilitado para tu proyecto."}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {superAdmin && <Chip icon={<PeopleAltRoundedIcon />} label="Usuarios" color="success" />}
              <Chip label="Proyectos" color="success" />
              <Chip label="Productos" color="success" />
              <Chip label="Estadísticas después" variant="outlined" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
